import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

const StyledDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...", 
  className = "",
  icon: Icon = null,
  showClear = false,
  disabled = false,
  searchPlaceholder = "Search...",
  multiple = false,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen]);

  const selectedValues = multiple
    ? (Array.isArray(value) ? value.map(String) : value ? [String(value)] : [])
    : [];
  const selectedOptions = multiple
    ? options.filter((opt) => selectedValues.includes(String(opt.value)) && opt.value !== '')
    : [];
  const selectedOption = multiple ? null : options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const isPlaceholder = multiple ? selectedOptions.length === 0 : !selectedOption;

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const label = String(option.label ?? '').toLowerCase();
      const optionValue = String(option.value ?? '').toLowerCase();
      return label.includes(q) || optionValue.includes(q);
    });
  }, [options, searchTerm]);

  const handleSelect = (optionValue) => {
    if (multiple) {
      const nextValue = String(optionValue);
      const next = selectedValues.includes(nextValue)
        ? selectedValues.filter((item) => item !== nextValue)
        : [...selectedValues, nextValue];
      onChange(next);
      return;
    }
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(multiple ? [] : '');
    setSearchTerm('');
  };

  const handleRemoveSelected = (e, optionValue) => {
    e.stopPropagation();
    onChange(selectedValues.filter((item) => item !== String(optionValue)));
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-required={required || undefined}
        className={`w-full flex items-center justify-between neo-field dark:text-dark-text px-3 py-2 text-left ${multiple ? 'min-h-[42px]' : 'h-[42px]'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {Icon && <Icon size={16} className="text-gray-400 dark:text-dark-muted flex-shrink-0" />}
          {multiple ? (
            selectedOptions.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className="inline-flex items-center gap-1 max-w-full rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-2 py-0.5 text-xs"
                  >
                    <span className="truncate">{option.label}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleRemoveSelected(e, option.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleRemoveSelected(e, option.value);
                      }}
                      className="text-primary-500 hover:text-primary-800 dark:hover:text-primary-200"
                    >
                      <X size={12} />
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="truncate text-sm text-gray-400 dark:text-gray-500">{placeholder}</span>
            )
          ) : (
            <span className={`truncate text-sm ${isPlaceholder ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-dark-text'}`}>
              {displayValue}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {showClear && !required && (multiple ? selectedValues.length > 0 : value) && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text p-0.5"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown 
            size={16} 
            className={`text-gray-400 dark:text-dark-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 neo-raised max-h-80 overflow-hidden flex flex-col">
          <div className="p-2">
            <div className="relative">
              <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-2 text-sm neo-field dark:text-dark-text"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {filteredOptions.length > 0 ? (
              <div className="py-1">
                {filteredOptions.map((option) => {
                  const isSelected = multiple
                    ? selectedValues.includes(String(option.value))
                    : value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      {multiple ? (
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : 'border-gray-300 dark:border-slate-500'
                          }`}>
                            {isSelected ? '✓' : ''}
                          </span>
                          <span>{option.label}</span>
                        </span>
                      ) : (
                        option.label
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-dark-muted">
                {options.length === 0 ? 'No options available' : 'No matches found'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StyledDropdown;
